// #607: Dokument-Freigaben im Mieter-/Eigentümerportal (Tabelle dokument_freigaben, Migration F1).
// Ziel 'alle' = alle aktuellen Mieter bzw. Eigentümer des Objekts (allgemein, auch für Nachfolger),
// 'person' = ein Mieter/Eigentümer, 'einheit' = wer am Stichtag dort wohnt bzw. Eigentümer ist (persönlich).
// Geschrieben wird nur über die Datenbankfunktionen dokument_freigaben_setzen/_entziehen (Team, V000 nur mit Recht).
import { supabase } from '../supabase/client';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { mieterAnzeigename } from '../uebergabe/personenSuche';

export type FreigabePortal = 'mieter' | 'eigentuemer';

/** Fenster-Ereignis nach jeder Änderung (Listen-Abzeichen laden neu). */
export const FREIGABEN_GEAENDERT = 'immo-freigaben-geaendert';
export function meldeFreigabenGeaendert(): void { window.dispatchEvent(new Event(FREIGABEN_GEAENDERT)); }
export type FreigabeZiel = 'alle' | 'person' | 'einheit';

export interface Freigabe {
  id: string;
  dokument_id: string;
  portal: FreigabePortal;
  ziel: FreigabeZiel;
  objekt_id: string | null;
  mieter_id: string | null;
  eigentuemer_id: string | null;
  einheit_id: string | null;
  stichtag: string;
  freigegeben_von: string | null;
  freigegeben_am: string;
}

/** Was der Dialog über ein Dokument wissen muss. */
export interface FreigabeDok {
  id: string;
  titel: string;
  objekt_id: string | null;
  mieter_id: string | null;
  eigentuemer_id: string | null;
  kategorie?: string | null;
  dokument_typ?: string | null;
}

export interface EmpfObjekt { id: string; titel: string }
export interface EmpfEinheit { id: string; objektId: string; bezeichnung: string }
export interface EmpfPerson {
  id: string;
  name: string;
  objektIds: string[];
  einheitIds: string[];
  aktuell: boolean;
  profilId: string | null;
}
export interface Empfaenger { objekte: EmpfObjekt[]; einheiten: EmpfEinheit[]; mieter: EmpfPerson[]; eigentuemer: EmpfPerson[] }

/** Auswahl je Portal im Dialog. */
export interface PortalAuswahl { alle: boolean; personen: string[]; einheiten: string[] }
export const LEERE_AUSWAHL: PortalAuswahl = { alle: false, personen: [], einheiten: [] };

// ── Reine Logik ───────────────────────────────────────────────────────────────────────────────────────

/** Datum aus Text (YYYY-MM-DD… oder TT.MM.JJJJ) – wie portal_text_datum in der Datenbank. */
export function textDatum(t: string | null | undefined): string | null {
  if (!t) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(t);
  if (iso) return iso[1]!;
  const de = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(t.trim());
  return de ? `${de[3]}-${de[2]}-${de[1]}` : null;
}

/** Wie portal_mieter_aktuell: Status aktiv/gekündigt und Mietende nicht vorbei. */
export function mieterAktuell(status: string | null | undefined, mietende: string | null | undefined, heute: string): boolean {
  const s = (status ?? 'aktiv').trim().toLowerCase() || 'aktiv';
  if (!['aktiv', 'gekündigt', 'gekuendigt'].includes(s)) return false;
  const ende = textDatum(mietende);
  return !ende || ende >= heute;
}

/** Vertrag (vertrag_bis als Text oder Datum) läuft heute noch. */
export function vertragLaeuft(bis: string | null | undefined, heute: string): boolean {
  const ende = textDatum(bis);
  return !ende || ende >= heute;
}

const PERSOENLICH = /mietvertrag|abrechnung|mahnung|kündigung|kuendigung|schriftverkehr|selbstauskunft|ausweis|lohn|gehalt|zahlungserinnerung|vollmacht|übergabeprotokoll|uebergabeprotokoll/i;

/** Persönliches Dokument? Zugeordnet zu einem Mieter/Eigentümer oder Kategorie/Titel wie Mietvertrag, Abrechnung, Mahnung … */
export function istPersoenlich(d: FreigabeDok): boolean {
  if (d.mieter_id || d.eigentuemer_id) return true;
  return PERSOENLICH.test(`${d.kategorie ?? ''} ${d.dokument_typ ?? ''} ${d.titel}`);
}

/** Wer wird mit dieser Auswahl heute erreicht? (Personen-IDs; 'alle' = aktuelle Personen der Dokument-Objekte) */
export function erreichtePersonen(a: PortalAuswahl, personen: readonly EmpfPerson[], objektIds: readonly string[]): Set<string> {
  const r = new Set<string>();
  for (const p of personen) {
    if (!p.aktuell) {
      if (a.personen.includes(p.id)) r.add(p.id);
      continue;
    }
    if (a.alle && p.objektIds.some(o => objektIds.includes(o))) r.add(p.id);
    else if (a.personen.includes(p.id)) r.add(p.id);
    else if (p.einheitIds.some(e => a.einheiten.includes(e))) r.add(p.id);
  }
  return r;
}

export function istLeer(a: PortalAuswahl): boolean {
  return !a.alle && a.personen.length === 0 && a.einheiten.length === 0;
}

/** Ziele für dokument_freigaben_setzen. 'alle' ohne Objekt = Objekt des jeweiligen Dokuments. */
export function zieleAus(portal: FreigabePortal, a: PortalAuswahl): Record<string, string>[] {
  const z: Record<string, string>[] = [];
  if (a.alle) z.push({ ziel: 'alle' });
  for (const id of a.personen) z.push(portal === 'mieter' ? { ziel: 'person', mieter_id: id } : { ziel: 'person', eigentuemer_id: id });
  for (const id of a.einheiten) z.push({ ziel: 'einheit', einheit_id: id });
  return z;
}

/** Bestehende Freigaben eines Dokuments als Auswahl (zum Ändern). */
export function auswahlAus(freigaben: readonly Freigabe[], portal: FreigabePortal): PortalAuswahl {
  const f = freigaben.filter(x => x.portal === portal);
  return {
    alle: f.some(x => x.ziel === 'alle'),
    personen: f.filter(x => x.ziel === 'person').map(x => (portal === 'mieter' ? x.mieter_id : x.eigentuemer_id)).filter((x): x is string => !!x),
    einheiten: f.filter(x => x.ziel === 'einheit').map(x => x.einheit_id).filter((x): x is string => !!x),
  };
}

const PORTAL_WORT: Record<FreigabePortal, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer' };

/** Teil der Zusammenfassung je Portal, z. B. „alle Mieter“, „12 Mieter“ oder „2 Eigentümer, 1 Einheit“. */
export function auswahlText(portal: FreigabePortal, a: PortalAuswahl, erreicht: number): string {
  const w = PORTAL_WORT[portal];
  if (a.alle) return `alle ${w}`;
  if (istLeer(a)) return '';
  return erreicht === 0 && a.einheiten.length ? `${a.einheiten.length} ${a.einheiten.length === 1 ? 'Einheit' : 'Einheiten'} (${w}, derzeit niemand)` : `${erreicht} ${w}`;
}

/** „3 Dokumente an 12 Mieter und alle Eigentümer freigeben“ */
export function zusammenfassung(anzahlDoks: number, teile: readonly string[], aendern: boolean): string {
  const docs = anzahlDoks === 1 ? '1 Dokument' : `${anzahlDoks} Dokumente`;
  const an = teile.filter(Boolean);
  if (!an.length) return aendern ? `${docs}: alle Freigaben entziehen` : `${docs} – noch keine Empfänger gewählt`;
  const liste = an.length === 1 ? an[0] : `${an.slice(0, -1).join(', ')} und ${an[an.length - 1]}`;
  return `${docs} an ${liste} ${aendern ? 'freigeben (ersetzt die bisherigen Freigaben)' : 'freigeben'}`;
}

/** Kurzform für Abzeichen in Listen: „Mieter: alle“, „Mieter: 2“, „Eigentümer: 1“. */
export function freigabeKurz(freigaben: readonly Freigabe[], portal: FreigabePortal): string | null {
  const f = freigaben.filter(x => x.portal === portal);
  if (!f.length) return null;
  if (f.some(x => x.ziel === 'alle')) return `${PORTAL_WORT[portal]}: alle`;
  return `${PORTAL_WORT[portal]}: ${f.length}`;
}

// ── Datenbank ────────────────────────────────────────────────────────────────────────────────────────

function heuteIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export async function ladeFreigaben(dokumentIds: readonly string[]): Promise<Freigabe[]> {
  if (!dokumentIds.length) return [];
  const { data, error } = await supabase.from('dokument_freigaben')
    .select('id, dokument_id, portal, ziel, objekt_id, mieter_id, eigentuemer_id, einheit_id, stichtag, freigegeben_von, freigegeben_am')
    .in('dokument_id', [...dokumentIds]).order('freigegeben_am');
  if (error) throw new Error(error.message);
  return (data ?? []) as Freigabe[];
}

/** Alle Freigaben der Dokumente eines Objekts (für Abzeichen und Filter im Aktenschrank). */
export async function ladeFreigabenFuerObjekt(objektId: string): Promise<Freigabe[]> {
  const { data, error } = await supabase.from('dokument_freigaben')
    .select('id, dokument_id, portal, ziel, objekt_id, mieter_id, eigentuemer_id, einheit_id, stichtag, freigegeben_von, freigegeben_am, dokumente!inner(objekt_id)')
    .eq('dokumente.objekt_id', objektId).limit(5000);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as (Freigabe & { dokumente: unknown })[]).map(({ dokumente: _d, ...f }) => f);
}

/** Freigaben je Dokument gruppiert. */
export function nachDokument(freigaben: readonly Freigabe[]): Map<string, Freigabe[]> {
  const m = new Map<string, Freigabe[]>();
  for (const f of freigaben) m.set(f.dokument_id, [...(m.get(f.dokument_id) ?? []), f]);
  return m;
}

/** Tooltip für das Abzeichen, z. B. „Mieter: alle des Objekts · Eigentümer: 1 Person, 1 Einheit“. */
export function freigabeTip(freigaben: readonly Freigabe[]): string {
  return (['mieter', 'eigentuemer'] as const).map(p => {
    const f = freigaben.filter(x => x.portal === p);
    if (!f.length) return '';
    const teile: string[] = [];
    if (f.some(x => x.ziel === 'alle')) teile.push('alle des Objekts');
    const pers = f.filter(x => x.ziel === 'person').length;
    const ein = f.filter(x => x.ziel === 'einheit').length;
    if (pers) teile.push(`${pers} ${pers === 1 ? 'Person' : 'Personen'}`);
    if (ein) teile.push(`${ein} ${ein === 1 ? 'Einheit' : 'Einheiten'}`);
    return `${PORTAL_WORT[p]}: ${teile.join(', ')}`;
  }).filter(Boolean).join(' · ');
}

/** Freigeben (ersetzen = bisherige Freigaben dieses Portals vorher entfernen). Liefert Fehlermeldung oder null. */
export async function setzeFreigaben(dokumentIds: readonly string[], portal: FreigabePortal, a: PortalAuswahl, ersetzen: boolean): Promise<string | null> {
  if (!ersetzen && istLeer(a)) return null;
  const { error } = await supabase.rpc('dokument_freigaben_setzen', {
    p_dokument_ids: [...dokumentIds], p_portal: portal, p_ziele: zieleAus(portal, a), p_ersetzen: ersetzen,
  });
  return error ? error.message : null;
}

/** Ein Dokument genau einer Person freigeben (Schreiben-Assistent, Zustellweg „Portal“) – nie „alle des Objekts“. */
export async function freigebenAnPerson(dokumentId: string, portal: FreigabePortal, personId: string): Promise<string | null> {
  return setzeFreigaben([dokumentId], portal, { alle: false, personen: [personId], einheiten: [] }, false);
}

export async function entziehe(dokumentIds: readonly string[], portal: FreigabePortal | null): Promise<string | null> {
  const { error } = await supabase.rpc('dokument_freigaben_entziehen', { p_dokument_ids: [...dokumentIds], p_portal: portal });
  return error ? error.message : null;
}

type NameRoh = { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; anrede?: string | null };

/** Mögliche Empfänger zu den Objekten der Dokumente (+ direkt zugeordnete Personen). */
export async function ladeEmpfaenger(doks: readonly FreigabeDok[]): Promise<Empfaenger> {
  const heute = heuteIso();
  const objektIds = [...new Set(doks.map(d => d.objekt_id).filter((x): x is string => !!x))];
  const extraMieter = [...new Set(doks.map(d => d.mieter_id).filter((x): x is string => !!x))];
  const extraEig = [...new Set(doks.map(d => d.eigentuemer_id).filter((x): x is string => !!x))];
  const MF = 'id, vorname, nachname, firma_name, objekt_id, einheit_id, status, mietende, profile_id';
  const [obj, ein, mie, mieExtra, oe] = await Promise.all([
    objektIds.length ? supabase.from('objekte').select('id, objektnummer, bezeichnung').in('id', objektIds) : Promise.resolve({ data: [], error: null }),
    objektIds.length ? supabase.from('objekt_einheiten').select('id, objekt_id, bezeichnung').in('objekt_id', objektIds) : Promise.resolve({ data: [], error: null }),
    objektIds.length ? supabase.from('mieter').select(MF).in('objekt_id', objektIds).or('status.is.null,status.neq.interessent') : Promise.resolve({ data: [], error: null }),
    extraMieter.length ? supabase.from('mieter').select(MF).in('id', extraMieter) : Promise.resolve({ data: [], error: null }),
    objektIds.length ? supabase.from('objekt_eigentuemer').select('objekt_id, eigentuemer_id, vertrag_bis').in('objekt_id', objektIds) : Promise.resolve({ data: [], error: null }),
  ]);
  for (const r of [obj, ein, mie, mieExtra, oe]) if (r.error) throw new Error(r.error.message);
  const einheiten = ((ein.data ?? []) as { id: string; objekt_id: string; bezeichnung: string | null }[])
    .map(e => ({ id: e.id, objektId: e.objekt_id, bezeichnung: e.bezeichnung || 'Einheit' }))
    .sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung, 'de', { numeric: true }));
  const ee = einheiten.length
    ? await supabase.from('einheit_eigentuemer').select('einheit_id, eigentuemer_id, vertrag_bis').in('einheit_id', einheiten.map(e => e.id))
    : { data: [], error: null };
  if (ee.error) throw new Error(ee.error.message);
  const einheitObjekt = new Map(einheiten.map(e => [e.id, e.objektId]));

  type MRoh = NameRoh & { objekt_id: string | null; einheit_id: string | null; status: string | null; mietende: string | null; profile_id: string | null };
  const mieterMap = new Map<string, EmpfPerson>();
  for (const m of [...(mie.data ?? []), ...(mieExtra.data ?? [])] as MRoh[]) {
    mieterMap.set(m.id, {
      id: m.id, name: mieterAnzeigename(m), objektIds: m.objekt_id ? [m.objekt_id] : [], einheitIds: m.einheit_id ? [m.einheit_id] : [],
      aktuell: mieterAktuell(m.status, m.mietende, heute), profilId: m.profile_id,
    });
  }

  const eigRel = new Map<string, { objektIds: Set<string>; einheitIds: Set<string>; aktuell: boolean }>();
  const rel = (id: string) => {
    let r = eigRel.get(id);
    if (!r) { r = { objektIds: new Set(), einheitIds: new Set(), aktuell: false }; eigRel.set(id, r); }
    return r;
  };
  for (const x of (oe.data ?? []) as { objekt_id: string; eigentuemer_id: string; vertrag_bis: string | null }[]) {
    const r = rel(x.eigentuemer_id);
    r.objektIds.add(x.objekt_id);
    if (vertragLaeuft(x.vertrag_bis, heute)) r.aktuell = true;
  }
  for (const x of (ee.data ?? []) as { einheit_id: string; eigentuemer_id: string; vertrag_bis: string | null }[]) {
    const r = rel(x.eigentuemer_id);
    r.einheitIds.add(x.einheit_id);
    const o = einheitObjekt.get(x.einheit_id);
    if (o) r.objektIds.add(o);
    if (vertragLaeuft(x.vertrag_bis, heute)) r.aktuell = true;
  }
  for (const id of extraEig) if (!eigRel.has(id)) { const r = rel(id); r.aktuell = true; }
  const eigIds = [...eigRel.keys()];
  const eigNamen = eigIds.length
    ? await supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, profile_id, status').in('id', eigIds)
    : { data: [], error: null };
  if (eigNamen.error) throw new Error(eigNamen.error.message);
  const eigentuemer: EmpfPerson[] = ((eigNamen.data ?? []) as (NameRoh & { profile_id: string | null; status: string | null })[]).map(e => {
    const r = eigRel.get(e.id)!;
    const aktiv = (e.status ?? 'aktiv').trim().toLowerCase() !== 'inaktiv';
    return { id: e.id, name: eigentuemerAnzeigename(e), objektIds: [...r.objektIds], einheitIds: [...r.einheitIds], aktuell: r.aktuell && aktiv, profilId: e.profile_id };
  });

  const nachName = (a: EmpfPerson, b: EmpfPerson) => a.name.localeCompare(b.name, 'de');
  return {
    objekte: ((obj.data ?? []) as { id: string; objektnummer: string | null; bezeichnung: string | null }[])
      .map(o => ({ id: o.id, titel: [o.objektnummer, o.bezeichnung].filter(Boolean).join(' · ') || 'Objekt' })),
    einheiten,
    mieter: [...mieterMap.values()].sort(nachName),
    eigentuemer: eigentuemer.sort(nachName),
  };
}

/** Anzeige einer Freigabe-Zeile, z. B. „Alle Mieter · 0012 Rosenweg“ oder „Max Müller“. */
export interface FreigabeAnzeige { freigabe: Freigabe; text: string; zusatz: string; von: string | null }

export async function ladeFreigabeAnzeige(dokumentId: string): Promise<{ zeilen: FreigabeAnzeige[]; abrufe: { name: string; erstmals: string; zuletzt: string; anzahl: number }[] }> {
  const freigaben = await ladeFreigaben([dokumentId]);
  const ids = (k: keyof Freigabe) => [...new Set(freigaben.map(f => f[k]).filter((x): x is string => typeof x === 'string'))];
  const abr = await supabase.from('dokument_abrufe').select('profil_id, erstmals_am, zuletzt_am, anzahl').eq('dokument_id', dokumentId);
  if (abr.error) throw new Error(abr.error.message);
  const abrufe = (abr.data ?? []) as { profil_id: string; erstmals_am: string; zuletzt_am: string; anzahl: number }[];
  const profilIds = [...new Set([...ids('freigegeben_von'), ...abrufe.map(a => a.profil_id)])];
  const leer = { data: [], error: null };
  const [obj, mie, eig, ein, prof, mieP, eigP] = await Promise.all([
    ids('objekt_id').length ? supabase.from('objekte').select('id, objektnummer, bezeichnung').in('id', ids('objekt_id')) : Promise.resolve(leer),
    ids('mieter_id').length ? supabase.from('mieter').select('id, vorname, nachname, firma_name').in('id', ids('mieter_id')) : Promise.resolve(leer),
    ids('eigentuemer_id').length ? supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name').in('id', ids('eigentuemer_id')) : Promise.resolve(leer),
    ids('einheit_id').length ? supabase.from('objekt_einheiten').select('id, bezeichnung').in('id', ids('einheit_id')) : Promise.resolve(leer),
    profilIds.length ? supabase.from('profiles').select('id, full_name').in('id', profilIds) : Promise.resolve(leer),
    abrufe.length ? supabase.from('mieter').select('profile_id, vorname, nachname, firma_name').in('profile_id', abrufe.map(a => a.profil_id)) : Promise.resolve(leer),
    abrufe.length ? supabase.from('eigentuemer').select('profile_id, anrede, vorname, nachname, firma_name').in('profile_id', abrufe.map(a => a.profil_id)) : Promise.resolve(leer),
  ]);
  const objName = new Map(((obj.data ?? []) as { id: string; objektnummer: string | null; bezeichnung: string | null }[]).map(o => [o.id, [o.objektnummer, o.bezeichnung].filter(Boolean).join(' ')]));
  const mieName = new Map(((mie.data ?? []) as NameRoh[]).map(m => [m.id, mieterAnzeigename(m)]));
  const eigName = new Map(((eig.data ?? []) as NameRoh[]).map(e => [e.id, eigentuemerAnzeigename(e)]));
  const einName = new Map(((ein.data ?? []) as { id: string; bezeichnung: string | null }[]).map(e => [e.id, e.bezeichnung || 'Einheit']));
  const profName = new Map(((prof.data ?? []) as { id: string; full_name: string | null }[]).map(p => [p.id, p.full_name || '']));
  const portalName = new Map<string, string>();
  for (const m of (mieP.data ?? []) as (NameRoh & { profile_id: string })[]) portalName.set(m.profile_id, mieterAnzeigename(m));
  for (const e of (eigP.data ?? []) as (NameRoh & { profile_id: string })[]) portalName.set(e.profile_id, eigentuemerAnzeigename(e));

  const zeilen = freigaben.map(f => {
    const w = PORTAL_WORT[f.portal];
    let text = w;
    let zusatz = '';
    if (f.ziel === 'alle') { text = `Alle ${w}`; zusatz = objName.get(f.objekt_id ?? '') ?? ''; }
    else if (f.ziel === 'person') text = (f.portal === 'mieter' ? mieName.get(f.mieter_id ?? '') : eigName.get(f.eigentuemer_id ?? '')) ?? `${w} (gelöscht)`;
    else { text = `Einheit ${einName.get(f.einheit_id ?? '') ?? ''}`.trim(); zusatz = `${w} am ${deDatum(f.stichtag)}`; }
    return { freigabe: f, text, zusatz, von: f.freigegeben_von ? profName.get(f.freigegeben_von) || null : null };
  });
  return {
    zeilen,
    abrufe: abrufe.map(a => ({ name: portalName.get(a.profil_id) || profName.get(a.profil_id) || 'Portal-Nutzer', erstmals: a.erstmals_am, zuletzt: a.zuletzt_am, anzahl: a.anzahl })),
  };
}

export function deDatum(iso: string | null | undefined): string {
  const t = textDatum(iso);
  return t ? `${t.slice(8, 10)}.${t.slice(5, 7)}.${t.slice(0, 4)}` : '';
}

/** #611: App-Dokumente zu Drive-Dateien (Suchtreffer) – für Auswahl und Freigabe. Map Drive-ID → Dokument. */
export async function ladeDokumenteZuDrive(driveIds: readonly string[]): Promise<Map<string, FreigabeDok>> {
  const m = new Map<string, FreigabeDok>();
  for (let i = 0; i < driveIds.length; i += 200) {
    const { data, error } = await supabase.from('dokumente')
      .select('id, gdrive_file_id, titel, datei_name, objekt_id, mieter_id, eigentuemer_id, kategorie, dokument_typ')
      .in('gdrive_file_id', driveIds.slice(i, i + 200)).eq('is_deleted', false);
    if (error) throw new Error(error.message);
    for (const d of (data ?? []) as (FreigabeDok & { gdrive_file_id: string; datei_name: string | null })[]) {
      m.set(d.gdrive_file_id, { id: d.id, titel: d.titel || d.datei_name || 'Dokument', objekt_id: d.objekt_id, mieter_id: d.mieter_id,
        eigentuemer_id: d.eigentuemer_id, kategorie: d.kategorie, dokument_typ: d.dokument_typ });
    }
  }
  return m;
}
